import { normalizePhoneDigits } from "@/lib/format-phone";
import { collectPhonesFromText, parseVCardToContact } from "@/lib/vcard-parser";

export interface RepairedSharedContact {
  name: string;
  phones: string[];
  emails: string[];
  org: string | null;
}

function phoneFromEntry(p: unknown): string {
  if (typeof p === "string" || typeof p === "number") return String(p).trim();
  if (!p || typeof p !== "object") return "";
  const o = p as Record<string, unknown>;
  return String(o.phone ?? o.wa_id ?? o.number ?? o.phoneNumber ?? "").trim();
}

function uniquePhones(values: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const digits = normalizePhoneDigits(raw);
    if (digits.length < 10 || digits.length > 15 || seen.has(digits)) continue;
    seen.add(digits);
    out.push(digits);
  }
  return out;
}

function phonesFromBody(body: string | null | undefined): string[] {
  if (!body) return [];
  const trimmed = body.trim();
  if (!trimmed || trimmed.startsWith("[")) return [];
  const colon = trimmed.indexOf(":");
  const tail = colon >= 0 ? trimmed.slice(colon + 1) : trimmed;
  const parts = tail.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
  const out: string[] = [];
  for (const part of parts) {
    const digits = normalizePhoneDigits(part);
    if (digits.length >= 10 && digits.length <= 15) out.push(digits);
  }
  return out;
}

function contactName(contact: Record<string, unknown>): string {
  const nameObj = contact.name && typeof contact.name === "object"
    ? (contact.name as Record<string, unknown>)
    : undefined;
  const formatted = [
    nameObj?.first_name,
    nameObj?.last_name,
  ].filter(Boolean).join(" ").trim();
  return (
    (typeof contact.name === "string" && contact.name.trim()) ||
    (nameObj ? String(nameObj.formatted_name || "") : "") ||
    formatted ||
    (typeof contact.fullName === "string" && contact.fullName.trim()) ||
    "Contato"
  );
}

/** Linha "Nome: 5511…" daquele contato. Cada linha numerada é usada uma vez. */
function takeNamedLinePhones(lines: string[], used: Set<number>, name: string): string[] {
  const wanted = name.trim();
  if (!wanted) return [];
  for (let i = 0; i < lines.length; i++) {
    if (used.has(i)) continue;
    const trimmed = lines[i].trim();
    const colon = trimmed.indexOf(":");
    if (colon <= 0 || trimmed.slice(0, colon).trim() !== wanted) continue;
    const phones = phonesFromBody(trimmed);
    if (phones.length === 0) continue;
    used.add(i);
    return phones;
  }
  return [];
}

/**
 * Reconstrói telefones de cartões já persistidos (vCard agrupado, telefone
 * como string, waid/TEL no JSON do próprio contato, ou a linha "Nome: 5511…").
 * A mensagem inteira só entra quando há um único contato.
 */
export function repairSharedContacts(
  metadata: unknown,
  body?: string | null,
): RepairedSharedContact[] {
  const meta = metadata && typeof metadata === "object" ? (metadata as Record<string, unknown>) : {};
  const list = Array.isArray(meta.contacts) ? meta.contacts : [];
  const blobPhones = list.length === 1
    ? uniquePhones([
        ...collectPhonesFromText(typeof metadata === "string" ? metadata : JSON.stringify(metadata ?? "")),
        ...phonesFromBody(body),
        ...collectPhonesFromText(body),
      ])
    : [];
  const lines = body ? body.split(/\r?\n/) : [];
  const usedLines = new Set<number>();

  const out: RepairedSharedContact[] = [];
  for (const raw of list) {
    const c = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const name = contactName(c);
    const phones: string[] = [];
    for (const p of Array.isArray(c.phones) ? c.phones : []) {
      const rawPhone = phoneFromEntry(p);
      if (rawPhone) phones.push(rawPhone);
    }
    const extra = phoneFromEntry(c.phoneNumber ?? c.phone);
    if (extra) phones.push(extra);
    if (typeof c.vcard === "string") {
      const fromVcard = parseVCardToContact(c.vcard);
      if (fromVcard) phones.push(...fromVcard.phones);
    }
    const linePhones = takeNamedLinePhones(lines, usedLines, name);
    let merged = uniquePhones(phones);
    if (merged.length === 0) {
      merged = uniquePhones([
        ...collectPhonesFromText(JSON.stringify(c)),
        ...linePhones,
      ]);
    }
    if (merged.length === 0 && blobPhones.length > 0) merged = blobPhones;
    const emails: string[] = [];
    for (const e of Array.isArray(c.emails) ? c.emails : []) {
      const email = String((e as { email?: string })?.email ?? e ?? "").trim();
      if (email) emails.push(email);
    }
    const orgObj = c.org;
    const org =
      orgObj && typeof orgObj === "object"
        ? String((orgObj as { company?: string }).company || "") || null
        : typeof orgObj === "string"
          ? orgObj
          : null;
    out.push({ name: String(name).trim() || "Contato", phones: merged, emails, org });
  }
  return out;
}
